/* eslint-disable react-hooks/exhaustive-deps */
import { FormEvent, useEffect, useState } from "react";
import { Box, Button, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useLazyGetIncomeDocumentsConfigQuery } from "@api/catalogs";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { InactivityHandler } from "@components/InactivityHandler";
import { ProgressBar } from "@components/ProgressBar";
import CustomRightImage from "@components/CustomRightImagev2";
import { BOFU_TABS_CONFIG, STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import useExpedienteDocuments from "@hooks/useExpedienteDocuments";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { BofuTabDocument } from "@interfaces/index";
import { ApiError } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setLoanStatus } from "@store/slices/registerSlice";
import { RootState, useAppDispatch } from "@store/store";
import { UUID } from "node:crypto";
import {
  generateDocumentConfig,
  validateDocumentStructure,
  validateRequiredDocumentsAreUploaded,
} from "@components/TabBofu/functions";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import { CiecValidatorRequest, useCiecValidatorMutation } from "@api/sat_apis_new/satApsi";

const SCREEN_STATUS = "CSDOS";

const DocumentosCiec = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus: naiveNextStatus } = useStatusConfig(SCREEN_STATUS);
  const { nextStatus: afterCiecStatus } = useStatusConfig(STATUS_CATALOG.consulta_ciec.code);
  // Según el producto, CSDOS (documentos_sat) puede tener o no un paso de buró (ej. CSABE)
  // antes de CIEC. Si el siguiente paso natural ya es CIEC, se salta hasta lo que sigue después
  // de CIEC (la rama de documentos no debe pasar por esa pantalla); si el siguiente paso natural
  // es otro (ej. CSABE), se respeta tal cual, sin tocar la secuencia del backend.
  const nextStatus = naiveNextStatus?.code === STATUS_CATALOG.consulta_ciec.code ? afterCiecStatus : naiveNextStatus;

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const brand = useSelector((state: RootState) => state.brand);
  const companyData = useSelector((state: RootState) => state.register.company);

  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [triggerGetIncomeDocumentsConfig] = useLazyGetIncomeDocumentsConfigQuery();
  const [ciecValidation] = useCiecValidatorMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { expedienteDocuments, setExpedienteDocuments, handleUpdateDocument, handleUpdateDocumentMultiple } =
    useExpedienteDocuments();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    try {
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }

      setIsSubmitting(true);

      const payload_ciec: CiecValidatorRequest = {
        rfc: companyData.rfc || "",
        ciec: "",
        application_code: applicationData.company_application_code,
        client_details_id: String(applicationData.client_details_id),
        loan_application_conditions_id: String(applicationData.application_condition_id),
        loan_application_id: String(applicationData.id),
        requested_amount: Number(applicationData.loan_amount),
        requested_term: Number(applicationData.loan_term),
        validate_ciec: false,
        company_id: brand?.id ?? "",
        connection_is_mobile_app: true,
      };

      if (
        applicationData.tax_system_type === TAX_SYSTEM_TYPE.PF.code ||
        applicationData.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code
      ) {
        await ciecValidation(payload_ciec).unwrap();
      }

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setLoanStatus(nextStatus.code));
      navigate(nextStatus.url);
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-submit",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInitialData = async () => {
    try {
      setIsLoading(true);

      const [documents, incomeDocumentsConfig] = await Promise.all([
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          clientDetailsId: personalData.id,
          status: true,
        }).unwrap(),
        triggerGetIncomeDocumentsConfig({ tax_systems_id: applicationData.tax_system_id as string }).unwrap(),
      ]);

      const docs_account = documents.filter((doc) => doc.client_details_id === applicationData.client_details_id);

      const valid_tab_documents: BofuTabDocument[] = [];

      incomeDocumentsConfig?.documents
        .filter((item) => validateDocumentStructure(item))
        .forEach((docItem) => {
          const document_value = generateDocumentConfig({
            docItem,
            docs_account,
            docs_aval: [],
            clientDetailsId: applicationData.client_details_id as UUID,
            aval: undefined,
          });

          valid_tab_documents.push(document_value);
        });

      setExpedienteDocuments(valid_tab_documents);
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-getInitialData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    setCanContinue(!isLoading && !isSubmitting && validateRequiredDocumentsAreUploaded(expedienteDocuments));
  }, [expedienteDocuments, isLoading, isSubmitting]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />

        <Box
          sx={{
            mt: 2,
            mx: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box maxWidth="sm" width="100%">
            <Typography variant="overline" fontWeight={700} letterSpacing={1.6} color="primary.main">
              EMPRESA · DOCUMENTOS
            </Typography>

            <Typography variant="tofu_title" textAlign="left" color="primary.dark">
              Sube los documentos de tu empresa
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={1} color="primary.dark">
              Necesitamos estos documentos para evaluar tu solicitud de crédito.
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, px: "0 !important" }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: 3, mb: 2, px: "0 !important" }}>
            <form onSubmit={handleSubmit}>
              <DocumentsTabBofu
                inputPrefix={`cliente`}
                tabCode={BOFU_TABS_CONFIG.tab_representante_pf.code}
                documents={expedienteDocuments}
                onChange={handleUpdateDocument}
                onChangeMultiple={handleUpdateDocumentMultiple}
              />

              <Container maxWidth="sm" sx={{ mb: 2, mt: 2, px: "0 !important" }}>
                <Box display="flex" justifyContent="center">
                  <Button variant="default" color="primary" type="submit" disabled={!canContinue}>
                    Continuar
                  </Button>
                </Box>
              </Container>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default DocumentosCiec;
